import {
  isConditionGroup,
  OPERATORS_FOR,
  OPERATOR_LABELS,
  type Condition,
  type ConditionEntry,
  type FieldDefinition,
  type Operator,
} from '@shared/segments'

/**
 * The condition editor behind segments (Contacts), saved views (Inbox), and automation rules —
 * one engine, one UI, since all three are the same filter pointed at different rows.
 *
 * A top-level entry is a plain condition, or a group of them with its own match mode — one
 * level of nesting, no groups inside groups (`@shared/segments`' `ConditionGroup` is capped
 * the same way). That is enough to express "(category is Refunds or Delivery) and priority is
 * urgent" without a recursive tree editor, which the flat list this replaced could not say at
 * all.
 */
export function SegmentBuilder({
  fields,
  match,
  conditions,
  onChange,
}: {
  fields: FieldDefinition[]
  match: 'all' | 'any'
  conditions: ConditionEntry[]
  onChange: (next: { match: 'all' | 'any'; conditions: ConditionEntry[] }) => void
}) {
  const updateEntry = (index: number, next: ConditionEntry) => {
    onChange({ match, conditions: conditions.map((entry, i) => (i === index ? next : entry)) })
  }

  const addCondition = () => {
    onChange({
      match,
      conditions: [...conditions, { field: fields[0]?.key ?? '', operator: 'is', value: '' }],
    })
  }

  const addGroup = () => {
    onChange({
      match,
      conditions: [
        ...conditions,
        { match: 'any', conditions: [{ field: fields[0]?.key ?? '', operator: 'is', value: '' }] },
      ],
    })
  }

  const removeEntry = (index: number) => {
    onChange({ match, conditions: conditions.filter((_, i) => i !== index) })
  }

  return (
    <div className="stack gap-3">
      {conditions.length > 1 && (
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <span className="cad-muted">Match</span>
          <select
            className="cad-input"
            style={{ width: 'auto' }}
            value={match}
            onChange={(e) => onChange({ match: e.target.value as 'all' | 'any', conditions })}
          >
            <option value="all">all</option>
            <option value="any">any</option>
          </select>
          <span className="cad-muted">of the following</span>
        </div>
      )}

      {conditions.map((entry, index) =>
        isConditionGroup(entry) ? (
          <div
            key={index}
            className="stack gap-2"
            style={{ border: '1px solid var(--cad-line)', borderRadius: '8px', padding: '10px' }}
          >
            <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
              <div className="row gap-2" style={{ alignItems: 'center' }}>
                <span className="cad-muted">Match</span>
                <select
                  className="cad-input"
                  style={{ width: 'auto' }}
                  value={entry.match}
                  onChange={(e) => updateEntry(index, { ...entry, match: e.target.value as 'all' | 'any' })}
                >
                  <option value="all">all</option>
                  <option value="any">any</option>
                </select>
                <span className="cad-muted">of this group</span>
              </div>
              <button type="button" className="cad-btn" data-variant="text" onClick={() => removeEntry(index)}>
                Remove group
              </button>
            </div>

            {entry.conditions.map((condition, innerIndex) => (
              <ConditionRow
                key={innerIndex}
                fields={fields}
                condition={condition}
                onChange={(next) =>
                  updateEntry(index, {
                    ...entry,
                    conditions: entry.conditions.map((c, i) => (i === innerIndex ? next : c)),
                  })
                }
                onRemove={() =>
                  updateEntry(index, { ...entry, conditions: entry.conditions.filter((_, i) => i !== innerIndex) })
                }
              />
            ))}

            <button
              type="button"
              className="cad-btn"
              data-variant="quiet"
              onClick={() =>
                updateEntry(index, {
                  ...entry,
                  conditions: [...entry.conditions, { field: fields[0]?.key ?? '', operator: 'is', value: '' }],
                })
              }
            >
              Add condition to group
            </button>
          </div>
        ) : (
          <ConditionRow
            key={index}
            fields={fields}
            condition={entry}
            onChange={(next) => updateEntry(index, next)}
            onRemove={() => removeEntry(index)}
          />
        ),
      )}

      <div className="row gap-2 wrap">
        <button type="button" className="cad-btn" data-variant="quiet" onClick={addCondition}>
          Add condition
        </button>
        <button type="button" className="cad-btn" data-variant="quiet" onClick={addGroup}>
          Add group (or / and)
        </button>
      </div>
    </div>
  )
}

function ConditionRow({
  fields,
  condition,
  onChange,
  onRemove,
}: {
  fields: FieldDefinition[]
  condition: Condition
  onChange: (next: Condition) => void
  onRemove: () => void
}) {
  const fieldByKey = new Map(fields.map((field) => [field.key, field]))
  const field = fieldByKey.get(condition.field)
  const operators = field ? OPERATORS_FOR[field.type] : []
  const needsValue = condition.operator !== 'is_set' && condition.operator !== 'is_not_set'
  const isDays = condition.operator === 'within_days' || condition.operator === 'not_within_days'

  return (
    <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
      <select
        className="cad-input"
        style={{ width: 'auto' }}
        value={condition.field}
        onChange={(e) => {
          const nextField = fieldByKey.get(e.target.value)
          const nextOperators = nextField ? OPERATORS_FOR[nextField.type] : []
          onChange({ field: e.target.value, operator: nextOperators[0] ?? 'is', value: '' })
        }}
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        className="cad-input"
        style={{ width: 'auto' }}
        value={condition.operator}
        onChange={(e) => onChange({ ...condition, operator: e.target.value as Operator, value: '' })}
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </select>

      {needsValue &&
        (field?.options ? (
          <select
            className="cad-input"
            style={{ width: 'auto' }}
            value={condition.value ?? ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
          >
            <option value="">Choose…</option>
            {field.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="cad-input"
            style={{ width: isDays ? '80px' : '160px' }}
            value={condition.value ?? ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            placeholder={isDays ? 'days' : 'value'}
          />
        ))}

      <button type="button" className="cad-btn" data-variant="text" onClick={onRemove}>
        Remove
      </button>
    </div>
  )
}
